<template>
  <SearchBox
    v-model="fulltext"
    :label="label"
    name="search"
    :placeholder="$t('common:text-search-placeholder')"
    :on-submit="onSearch"
    :on-clear-search="clearSearch"
    :on-filter="onFilter"
    :on-speech="onSpeech"
  />
</template>

<script setup>
import _ from "lodash-es";
import { ROUTES } from "@utils/routes";
import { CONTENT_TYPE } from "@utils/constants";
import { useUIStore } from '@stores/ui';
import { useSelectedStore } from '@stores/selected'
import { useSearchStore } from '@stores/search';
import SearchBox from "@components/ui/search-box";

const windowSize = useWindowSize();

const route = useRoute();
const routeLang = useRouteLang();
const fulltext = ref(route?.query?.fulltext || '');

defineProps({
  label: {
    type: String,
    required: true,
    default: () => ''
  },
  isDetail: {
    type: Boolean,
    default: false
  }
});

const { $eventBus } = useNuxtApp();
const ui = useUIStore();
const store = useSelectedStore();
const search = useSearchStore();
const routeCategory = useRouteCategory();
const geolocation = useGeolocation();

watch(() => route.query.fulltext, (value) => fulltext.value = value);

const closeSidebar = () => {
  $eventBus.emit('sidebar:close');
};

const reloadProductVariants = async () => {
  const { filters } = useVariantFilters();

  const body = _.pickBy(Object.assign(
    {},
    _.omit(filters.value, ['sort', 'direction', 'location', 'maxKmFromHome', 'maxKmFromLocation']),
    !store.hasCurrentLocation() && filters.value.maxKmFromHome ? {
      maxKmFromHome: filters.value.maxKmFromHome
    } : {},
    store.hasCurrentLocation() ? {
      currentLocationLat: +(geolocation.coordinates.value.latitude || store.coordinates.latitude),
      currentLocationLng: +(geolocation.coordinates.value.longitude || store.coordinates.longitude)
    } : {},
    store.hasCurrentLocation() && filters.value.maxKmFromLocation ? {
      maxKmFromLocation: filters.value.maxKmFromLocation
    } : {},
    { fulltext: fulltext.value },
  ), value => value !== null && value !== '' && value !== 0);

  await loadProductVariants(
    {
      page: 1,
      limit: search.variants.pagination.itemsPerPage,
      sort: route.query?.sort || 'order',
      direction: route.query?.direction || 'desc',
      locale: routeLang.locale.value
    },
    body
  );
}

const reloadContentInspiration = async () => {
  const { filters } = useContentFilters();

  const body = _.pickBy(Object.assign(
    {},
    _.omit(filters.value, ['sort', 'direction']),
    { fulltext: fulltext.value },
  ), value => value !== null && value !== '' && value !== 0);

  await loadContentInspiration(
    {
      type: CONTENT_TYPE.INSPIRATION,
      page: 1,
      limit: search.variants.pagination.itemsPerPage,
      sort: route.query?.sort || 'publishedAt',
      direction: route.query?.direction || 'desc',
      locale: routeLang.locale.value
    },
    body
  );
}

const reloadContentNews = async () => {
  const { filters } = useContentFilters();

  const body = _.pickBy(Object.assign(
    {},
    _.omit(filters.value, ['sort', 'direction']),
    { fulltext: fulltext.value },
  ), value => value !== null && value !== '' && value !== 0);

  await loadContentInspiration(
    {
      type: CONTENT_TYPE.NEWS,
      page: 1,
      limit: search.variants.pagination.itemsPerPage,
      sort: route.query?.sort || 'order',
      direction: route.query?.direction || 'desc',
      locale: routeLang.locale.value
    },
    body
  );
}

const reloadCatalogue = async () => {
  const { filters } = useContentFilters();

  const body = _.pickBy(Object.assign(
    {},
    _.omit(filters.value, ['sort', 'direction', 'idSupplier']),
    { fulltext: fulltext.value },
  ), value => value !== null && value !== '' && value !== 0);

  await loadContentCatalogue(
    {
      page: 1,
      limit: search.variants.pagination.itemsPerPage,
      sort: route.query?.sort || 'order',
      direction: route.query?.direction || 'desc',
      locale: routeLang.locale.value
    },
    body
  );
}

const onSearch = _.debounce(async () => {
  if (ui.displaySidebar) {
    closeSidebar();
  }

  await reloadProductVariants();
  await reloadContentInspiration();
  await reloadContentNews();
  await reloadCatalogue();

  if (route.path !== translatePath(ROUTES.CODE.SEARCH)) {
    await navigatorTo({
      path: translatePath(ROUTES.CODE.SEARCH),
      query: _.assign(
        { fulltext: fulltext.value },
        routeCategory.idCategory.value ? { idCategory: routeCategory.idCategory.value } : {},
        routeCategory.idPostCategory.value ? { idPostCategory: routeCategory.idPostCategory.value } : {}
      )
    });
  } else {
    await useSearchParam('fulltext', fulltext.value);
  }
}, 500, { leading: false, trailing: true });

const clearSearch = async () => {
  fulltext.value = '';
  await useSearchParam('fulltext', fulltext.value);
}

const onFilter = async () => {
  if (windowSize?.width?.value < 1024) {
    $eventBus.emit('sidebar:open', 'FILTER_VIEW');
  } else if (import.meta.client) {
    scrollToView();
    $eventBus.emit('focus:filters');
  }
};

const onSpeech = () => {
  // TODO
  console.log('TODO.search.onSpeech');
};

</script>
